import { useState, useEffect, useMemo } from 'react';
import { Flex, Loader, Grid } from '@mantine/core';
import { ErrorModal, ModuleView, SuccessModal } from '@jadwalh/ui';
import { useIntl } from 'react-intl';
import { IntegrationContainer } from './styles';
import { ReactComponent as GA } from '../../../assets/images/svg/GA.svg';
import { ReactComponent as MP } from '../../../assets/images/svg/MP.svg';
import { ReactComponent as GTM } from '../../../assets/images/svg/GTM.svg';
import { ReactComponent as ZC } from '../../../assets/images/svg/ZC.svg';
import { ReactComponent as HC } from '../../../assets/images/svg/HC.svg';
import { ReactComponent as GC } from '../../../assets/images/svg/GC.svg';
import { ReactComponent as OC } from '../../../assets/images/svg/OC.svg';
import { ReactComponent as IC } from '../../../assets/images/svg/IC.svg';
import { ReactComponent as Z } from '../../../assets/images/svg/zoom.svg';
import { ReactComponent as GM } from '../../../assets/images/svg/GM.svg';
import { ReactComponent as TP } from '../../../assets/images/svg/zoom.svg';
import { ReactComponent as GMAIL } from '../../../assets/images/svg/gmail.svg';
import { ReactComponent as PAYTABS } from '../../../assets/images/svg/PayTabs.svg';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import instance from '../../../axios-instance';
import { ReactComponent as BackBtn } from '../../../assets/images/svg/back-btn.svg';
import { getPermissions, setZohoCrmUrl } from '../../../store/profile.slice';
import { useDispatch, useSelector } from 'react-redux';
import AddIdentitySkeleton from '../identity/AddIdentitySkeleton';
import ZohoCRM from './components/zohoCrm';
import { setIntegrationId } from '../../../store/zohoIntegrationId.slice';
import GoogleCalendar from './components/googleCalendar';
import GoogleMeet from './components/googleMeet';
import Gmail from './components/gmail';
import TabiPayment from './components/tabiPayment';
import { getProfileData } from '../../../store/profile.slice';
import Zoom from './components/zoom';
import PayTabsIntegration from './components/paytabs';

export default function Integrations() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const permissions = useSelector(getPermissions);
  const profile = useSelector(getProfileData);
  const dispatch = useDispatch();
  const [showErrorModal, setShowErrorModal] = useState(false); // State for the error modal
  const [searchParams] = useSearchParams();

  const { protocol, hostname, port } = window.location;
  let host = `${protocol}//${hostname}`;
  if (hostname === 'localhost') {
    host += `:${port}`;
  }

  const [zohoUrl, setZohoUrl] = useState('');
  const { id } = useParams();

  const isDetailView = [
    'activate-zoho-crm',
    'activate-google-calendar',
    'activate-google-meet',
    'activate-gmail',
    'activate-zoom',
    'activate-tabi-payment',
    'activate-paytabs',
  ].includes(id ?? '');
  const [matches, setMatches] = useState(
    window.matchMedia('(min-width: 768px)').matches
  );

  const {
    data: integrationData,
    isLoading,
    refetch,
  } = useQuery(['integrations'], () => instance.get(`app/integrations/list`), {
    select: (res) => {
      return res.data.data;
    },
  });

  // Refetch integrations data when returning to the main page
  useEffect(() => {
    if (!id) {
      refetch();
    }
  }, [id, refetch]);

  const iconComponent = (IconName: string) => {
    switch (IconName) {
      case 'GA':
        return <GA />;
      case 'GTM':
        return <GTM />;
      case 'MP':
        return <MP />;
      case 'GC':
        return <GC />;
      case 'OC':
        return <OC />;
      case 'IC':
        return <IC />;
      case 'ZC':
        return <ZC />;
      case 'HC':
        return <HC />;
      case 'Z':
        return <Z />;
      case 'GM':
        return <GM />;
      case 'G':
        return <GC />;
      case 'TP':
        return <TP />;
      case 'GMAIL':
        return <GMAIL width={20} height={20} />;
      case 'PAYTABS':
        return <PAYTABS width={20} height={20} />;
      default:
        return <GA />;
    }
  };

  // Helper to get the feature key for gating
  const getFeatureKeyForIntegration = (appName: string) => {
    switch (appName) {
      case 'ZC':
        return 'can_use_crm';
      case 'TP':
      case 'PAYTABS':
        return 'can_use_payments';
      case 'GMAIL':
        return 'can_use_gmail_integration';
      default:
        return null;
    }
  };

  const pageRedirect = (
    appName: string,
    url: string,
    isActive: boolean,
    integrationId?: number,
    email?: string
  ) => {
    if (appName === 'GC') {
      isActive
        ? navigate('/settings/calendar-sync')
        : navigate('/settings/integration/activate-google-calendar');
    } else if (appName === 'GM') {
      isActive
        ? navigate('/settings/calendar-sync')
        : navigate('/settings/integration/activate-google-meet');
    } else if (appName === 'GMAIL') {
      if (email) {
        localStorage.setItem('gmailEmail', email);
      }
      isActive
        ? navigate('/settings/integration/activate-gmail?is_active=true')
        : navigate('/settings/integration/activate-gmail?is_active=false');
    } else if (appName === 'ZC') {
      if (!isActive || profile.role.role_value === 2) {
        localStorage.setItem('zohoCrmUrl', url);
        navigate('/settings/integration/activate-zoho-crm');
      } else {
        integrationId && dispatch(setIntegrationId({ integrationId }));
        navigate('/settings/integration/zoho-crm');
      }
    } else if (appName === 'TP') {
      navigate('/settings/integration/activate-tabi-payment');
    } else if (appName === 'PAYTABS') {
      navigate('/settings/integration/activate-paytabs');
    } else {
      return url ? (window.location.href = url) : '';
    }
  };
  // else if (appName === 'Z') {
  //   setShowZoomContent(true);
  // }

  const permissionList = useMemo(() => {
    if (Array.isArray(permissions)) {
      return permissions;
    }

    if (Array.isArray(permissions?.data)) {
      return permissions.data.map((item: { name: string }) => item.name);
    }

    return [];
  }, [permissions]);

  const canListIntegrations = permissionList.includes('integration.list');
  const isProfileLoading = profile.id === -1;

  if (isProfileLoading) {
    return <AddIdentitySkeleton />;
  }

  if (!canListIntegrations) {
    return <AddIdentitySkeleton />;
  }

  return (
      <Grid mx={0}>
        <Grid.Col xs={12} p={0}>
          <IntegrationContainer
            style={{
              padding: matches ? '0px' : '16px 12px',
            }}
          >
            <Flex
              gap="12px"
              align={'center'}
              mb={{ base: '12px', md: '0' }}
              justify={'flex-start'}
              className="small-screen"
              w="100%"
            >
              <BackBtn
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (id) {
                    navigate('/settings/integration');
                  } else {
                    navigate('/settings');
                  }
                }}
              />
              <h2 className="SemiBold" style={{ marginTop: '5px' }}>
                {id === 'activate-zoho-crm'
                  ? 'Zoho CRM'
                  : id === 'activate-google-calendar'
                  ? 'Google Calendar'
                  : id === 'activate-google-meet'
                  ? 'Google Meet'
                  : id === 'activate-gmail'
                  ? 'Gmail'
                  : id === 'activate-zoom'
                  ? 'Zoom'
                  : id === 'activate-paytabs'
                  ? 'PayTabs'
                  : formatMessage({ id: 'Integration' })}
              </h2>
            </Flex>
            {!isDetailView ? (
              <Flex
                direction={'column'}
                justify={'center'}
                align={'center'}
                gap={'16px'}
                w={'100%'}
              >
                <ModuleView>
                  <Flex
                    direction={'column'}
                    align={'flex-start'}
                    justify={'center'}
                    gap={'13px'}
                    w="100%"
                    p={0}
                  >
                    <p className="SemiBold">
                      {formatMessage({ id: 'Integration' })}
                    </p>
                    {!isLoading &&
                      integrationData?.map((item: any) => (
                        <Flex
                          direction="column"
                          gap="16px"
                          justify={'flex-start'}
                          align={'flex-start'}
                          w={'100%'}
                        >
                          <p>{item.type}</p>
                          {item.categories.map((category: any) => {
                            const featureKey = getFeatureKeyForIntegration(category.key);

                            const integrationItem = (
                              <Flex
                                justify={'space-between'}
                                align={'flex-start'}
                                w="100%"
                                key={category.key}
                              >
                                <Flex
                                  gap={'12px'}
                                  justify={'flex-start'}
                                  align={'center'}
                                  w="100%"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    pageRedirect(
                                      category.key,
                                      category.url,
                                      category.is_active,
                                      category.integration_id,
                                      category.email
                                    );
                                  }}
                                >
                                  {iconComponent(category.key)}
                                  <p className="categoryName">{category.name}</p>
                                </Flex>
                                {!category.is_active &&
                                  (category.name === 'Google analytics' ||
                                    category.name === 'Outlook calendar' ||
                                    category.name === 'Icloud calendar' ||
                                    category.name === 'Zoom') && (
                                    <label className="deactive">
                                      {formatMessage({ id: 'soon' })}
                                    </label>
                                  )}
                              </Flex>
                            );

                            return integrationItem;
                          })}
                        </Flex>
                      ))}
                    {isLoading && (
                      <Flex
                        justify={'center'}
                        align={'center'}
                        w={'100%'}
                        my="lg"
                      >
                        <Loader color="#303030" size={'lg'} />
                      </Flex>
                    )}
                  </Flex>
                </ModuleView>
                <p>
                  هل تود التكامل مع برنامج اخر ؟{' '}
                  <a
                    href="https://api.whatsapp.com/send?phone=966533501119"
                    rel="noreferrer"
                    target="_blank"
                    style={{ color: '#0C58DB' }}
                  >
                    اضغط هنا
                  </a>
                </p>
              </Flex>
            ) : (
              <>
                {id === 'activate-zoho-crm' && <ZohoCRM />}
                {id === 'activate-google-calendar' && <GoogleCalendar />}
                {id === 'activate-google-meet' && <GoogleMeet />}
                {id === 'activate-gmail' && <Gmail refetch={refetch} />}
                {id === 'activate-tabi-payment' && <TabiPayment />}
                {id === 'activate-zoom' && <Zoom />}
                {id === 'activate-paytabs' && <PayTabsIntegration />}
              </>
            )}
          </IntegrationContainer>
        </Grid.Col>
      </Grid>
  );
}
